/**
 * The workout-frequency calendar: a year of days as a grid of shaded squares,
 * one column per week, so consistency is visible at a glance.
 *
 * Built with createElement rather than an innerHTML string: exercise names are
 * user-supplied and appear in every cell's tooltip, and there are several
 * hundred cells, so textContent is both the safe and the cheaper option.
 */
import { maybe, must } from '../dom';
import {
  buildCalendarGrid,
  countWorkoutsByDay,
  describeCount,
  workoutYears,
  type CalendarCell,
  type DayIndex,
} from '../lib/calendar';
import { shortDisplayName } from '../lib/format';
import { workoutData } from '../lib/store';

/**
 * Survives a re-render. The calendar is redrawn after every log and every
 * sync, and snapping the dropdown back to the current year each time would
 * yank the view out from under someone browsing last year.
 */
let selectedYear: number | null = null;

/** Index 0 is Sunday. Only alternate days are labelled, as GitHub's are. */
const WEEKDAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''] as const;

function describeDay(cell: CalendarCell, index: DayIndex): string {
  if (!cell.date || !cell.key) return '';

  const when = cell.date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  if (cell.count === 0) return `No workouts on ${when}`;

  const byExercise = index.get(cell.key)?.byExercise ?? {};
  const breakdown = Object.entries(byExercise)
    .sort(([, a], [, b]) => b - a)
    .map(([name, count]) => `${shortDisplayName(name)} ×${count}`)
    .join(', ');

  return `${describeCount(cell.count)} on ${when} — ${breakdown}`;
}

function buildCell(cell: CalendarCell, index: DayIndex): HTMLElement {
  // A div, not a button: 365 focusable controls would make keyboard traversal
  // of the rest of the page impractical, and the readout below the grid
  // carries the same information for anyone who cannot hover.
  const el = document.createElement('div');
  el.className = 'cal-cell';

  if (!cell.key) {
    el.classList.add('cal-cell-pad');
    el.setAttribute('aria-hidden', 'true');
    return el;
  }

  el.dataset['day'] = cell.key;

  // A blank future, rather than a long tail of "0 workouts" days that reads
  // like a month of missed sessions.
  if (cell.future) {
    el.classList.add('cal-cell-future');
    return el;
  }

  el.classList.add(`cal-level-${cell.level}`);
  if (cell.isToday) el.classList.add('cal-cell-today');
  el.title = describeDay(cell, index);

  return el;
}

function buildLegend(): HTMLElement {
  const legend = document.createElement('div');
  legend.className = 'cal-legend';

  const less = document.createElement('span');
  less.textContent = 'Less';
  legend.appendChild(less);

  for (const level of [0, 1, 2, 3, 4] as const) {
    const swatch = document.createElement('span');
    swatch.className = `cal-cell cal-level-${level}`;
    if (level === 0) swatch.title = 'No workouts';
    else if (level === 4) swatch.title = '4 or more workouts';
    else swatch.title = describeCount(level);
    legend.appendChild(swatch);
  }

  const more = document.createElement('span');
  more.textContent = 'More';
  legend.appendChild(more);

  return legend;
}

export function updateCalendarView(): void {
  const container = maybe('calendar-view');
  if (!container) return;

  const index = countWorkoutsByDay(workoutData);
  const years = workoutYears(workoutData);

  // A year can leave the list -- every workout in it deleted, or a restore
  // from a backup that predates it -- so fall back rather than render an empty
  // grid for a year no longer on offer.
  if (selectedYear === null || !years.includes(selectedYear)) {
    selectedYear = years[0] ?? new Date().getFullYear();
  }

  const grid = buildCalendarGrid(selectedYear, index);
  container.innerHTML = '';

  // --- heading and year picker --------------------------------------------

  const head = document.createElement('div');
  head.className = 'cal-head';

  const heading = document.createElement('div');

  const title = document.createElement('div');
  title.className = 'cal-title';
  const dayWord = grid.activeDays === 1 ? 'day' : 'days';
  title.textContent =
    grid.total === 0
      ? `No workouts logged in ${grid.year}`
      : `${describeCount(grid.total)} on ${grid.activeDays} ${dayWord} in ${grid.year}`;
  heading.appendChild(title);

  // Said out loud: this is the one panel that ignores the exercise dropdown,
  // and without saying so its totals look like they contradict the stat cards.
  const scope = document.createElement('div');
  scope.className = 'cal-scope';
  scope.textContent = 'All exercises';
  heading.appendChild(scope);

  head.appendChild(heading);

  const picker = document.createElement('select');
  picker.className = 'cal-year';
  picker.id = 'calendar-year-select';
  picker.setAttribute('aria-label', 'Year');
  for (const year of years) {
    const option = document.createElement('option');
    option.value = String(year);
    option.textContent = String(year);
    picker.appendChild(option);
  }
  picker.value = String(grid.year);
  picker.addEventListener('change', () => {
    selectedYear = Number(picker.value);
    updateCalendarView();
  });
  head.appendChild(picker);

  container.appendChild(head);

  // --- the grid -------------------------------------------------------------

  const body = document.createElement('div');
  body.className = 'cal-body';

  const weekdays = document.createElement('div');
  weekdays.className = 'cal-weekdays';
  weekdays.setAttribute('aria-hidden', 'true');
  for (const label of WEEKDAY_LABELS) {
    const row = document.createElement('div');
    row.className = 'cal-weekday';
    row.textContent = label;
    weekdays.appendChild(row);
  }
  body.appendChild(weekdays);

  // 53 columns is wider than a phone, so the graph scrolls inside itself
  // rather than making the whole page scroll sideways.
  const scroll = document.createElement('div');
  scroll.className = 'cal-scroll';

  const columns = grid.weeks.length;

  const months = document.createElement('div');
  months.className = 'cal-months';
  months.style.setProperty('--cal-columns', String(columns));
  for (const { column, label } of grid.monthLabels) {
    const el = document.createElement('div');
    el.className = 'cal-month';
    el.style.gridColumnStart = String(column + 1);
    el.textContent = label;
    months.appendChild(el);
  }
  scroll.appendChild(months);

  const cells = document.createElement('div');
  cells.className = 'cal-grid';
  cells.style.setProperty('--cal-columns', String(columns));
  for (const week of grid.weeks) {
    for (const cell of week) cells.appendChild(buildCell(cell, index));
  }
  scroll.appendChild(cells);

  body.appendChild(scroll);
  container.appendChild(body);

  // --- readout and legend ---------------------------------------------------

  const readout = document.createElement('div');
  readout.className = 'cal-readout';
  readout.id = 'calendar-readout';
  readout.textContent =
    grid.total === 0 ? 'Log a workout and it shows up here.' : 'Tap a square to see that day.';
  container.appendChild(readout);

  // Delegated, so one listener covers every cell. `title` alone would leave
  // this unusable on a phone, where there is no hover.
  cells.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('.cal-cell');
    const key = target?.dataset['day'];
    if (!target || !key) return;

    for (const selected of cells.querySelectorAll('.cal-cell-selected')) {
      selected.classList.remove('cal-cell-selected');
    }
    target.classList.add('cal-cell-selected');

    const cell = grid.weeks.flat().find((c) => c.key === key);
    if (cell) readout.textContent = describeDay(cell, index);
  });

  container.appendChild(buildLegend());
}

/** Renders, then scrolls the current year to its most recent end. */
export function showCalendarView(): void {
  updateCalendarView();

  // Only meaningful once the tab is visible: a display:none element has no
  // scrollWidth, so this cannot be done during the initial render.
  const scroll = must('calendar-view').querySelector<HTMLElement>('.cal-scroll');
  if (scroll && selectedYear === new Date().getFullYear()) {
    scroll.scrollLeft = scroll.scrollWidth;
  }
}
